import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ForkliftIcon from "@mui/icons-material/Agriculture";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../components/UI/States";
import { useLocations } from "../../components/Fleet/Forklifts/Utilities/useLocations";
import {
  BackLink,
  BranchSelect,
  Notice,
  Pill,
  ServiceChip,
  SearchField,
  serviceSummary,
} from "../../components/Fleet/FleetParts";
import {
  HISTORY_DAYS,
  checkinTone,
  compareUnits,
  daysAgo,
  formatDateTime,
  relativeDay,
} from "../../components/Fleet/fleetFormat";
import { AddForkliftDialog } from "../../components/Fleet/ForkliftDialogs";
import { fetchForklifts } from "./fleetApi";

// Every active forklift with its last check-in and 90-day counts from both
// the mobile app and the branch kiosk. Filters live in the URL
// (?branch=&status=&q=) so links from the Fleet home, the old Forklift
// Manager pages and the FAQ land pre-filtered.

// The last check-in we know of: newest inspection in the 90-day window,
// else the unit's own lastChecked stamp (older than the window).
export const lastCheckIn = (forklift) => forklift.stats?.lastAt || forklift.lastChecked;

const STATUSES = {
  all: { label: "All forklifts", test: () => true },
  in: { label: "In service", test: (f) => f.inService },
  out: { label: "Out of service", test: (f) => !f.inService },
  today: { label: "Checked today", test: (f) => daysAgo(lastCheckIn(f)) === 0 },
  quiet: {
    label: "No check-in 7+ days",
    hint: "In service",
    test: (f) => {
      const days = daysAgo(lastCheckIn(f));
      return f.inService && (days === null || days > 7);
    },
  },
  flagged: { label: "Flagged", hint: `Last ${HISTORY_DAYS} days`, test: (f) => f.stats.flagged > 0 },
};
const STATUS_TONES = { in: "emerald", out: "rose", today: "sky", quiet: "amber", flagged: "amber" };

const SORTS = {
  branch: (a, b) => a.location.localeCompare(b.location) || compareUnits(a.unit, b.unit),
  unit: (a, b) => compareUnits(a.unit, b.unit) || a.location.localeCompare(b.location),
  last: (a, b) => String(lastCheckIn(a) || "").localeCompare(String(lastCheckIn(b) || "")),
  count: (a, b) => a.stats.count - b.stats.count,
  flagged: (a, b) => a.stats.flagged - b.stats.flagged,
};

const SortHeader = ({ id, sort, onSort, children, className = "" }) => {
  const active = sort.id === id;
  return (
    <th className={className} aria-sort={active ? (sort.desc ? "descending" : "ascending") : undefined}>
      <button
        type="button"
        onClick={() => onSort(id)}
        className="inline-flex items-center gap-1 font-semibold hover:text-[color:var(--app-text)]"
      >
        {children}
        {active &&
          (sort.desc ? (
            <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} />
          ) : (
            <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} />
          ))}
      </button>
    </th>
  );
};

const ForkliftList = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { locations } = useLocations();
  const [forklifts, setForklifts] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [sort, setSort] = useState({ id: "branch", desc: false });

  const branch = params.get("branch") || "";
  const status = STATUSES[params.get("status")] ? params.get("status") : "all";
  const query = params.get("q") || "";

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchForklifts(user.id);
      setForklifts(data.forklifts);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const locationNames = useMemo(
    () => locations.map((l) => l.location).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [locations]
  );

  // Branches that actually have forklifts, plus every Location Manager branch.
  const branches = useMemo(() => {
    const names = new Set(locationNames);
    (forklifts || []).forEach((f) => f.location && names.add(f.location));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [forklifts, locationNames]);

  const inBranch = useMemo(
    () =>
      (forklifts || []).filter(
        (f) => !branch || f.location.toLowerCase() === branch.toLowerCase()
      ),
    [forklifts, branch]
  );

  const counts = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(STATUSES).map(([key, s]) => [key, inBranch.filter(s.test).length])
      ),
    [inBranch]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = inBranch
      .filter(STATUSES[status].test)
      .filter(
        (f) =>
          !q ||
          [f.unit, f.location, f.stats.lastInspector, f.oldUnit, f.service?.reasonLabel, f.service?.note]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q))
      );
    const compare = SORTS[sort.id] || SORTS.branch;
    return rows.sort((a, b) => (sort.desc ? -compare(a, b) : compare(a, b)));
  }, [inBranch, status, query, sort]);

  const onSort = (id) =>
    setSort((current) =>
      current.id === id
        ? { id, desc: !current.desc }
        : { id, desc: id === "last" || id === "count" || id === "flagged" }
    );

  return (
    <PageShell size="2xl">
      <AddForkliftDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(id) => {
          setAdding(false);
          navigate(`/fleet/forklifts/${id}`);
        }}
        locations={locationNames}
        userId={user?.id}
        defaultBranch={branch}
      />

      <BackLink to="/fleet">Fleet</BackLink>
      <PageHeader
        icon={ForkliftIcon}
        tone="teal"
        eyebrow="Fleet"
        title="Forklifts"
        description={`Every forklift with its last check-in from the mobile app or branch kiosk, and ${HISTORY_DAYS} days of history.`}
        actions={
          <>
            <button type="button" className="app-btn app-btn-secondary" onClick={load} disabled={loading}>
              <RefreshRoundedIcon sx={{ fontSize: 18 }} /> Refresh
            </button>
            <button type="button" className="app-btn app-btn-primary" onClick={() => setAdding(true)}>
              <AddRoundedIcon sx={{ fontSize: 18 }} /> Add forklift
            </button>
          </>
        }
      />

      {error && <Notice tone="rose">{error}</Notice>}

      {!forklifts && !error && <LoadingState label="Loading forklifts…" />}

      {forklifts && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {Object.entries(STATUSES).map(([key, s]) => (
              <StatTile
                key={key}
                label={s.label}
                value={counts[key]}
                hint={s.hint}
                tone={key === "all" ? "default" : STATUS_TONES[key]}
                active={status === key}
                onClick={() => setParam("status", key)}
              />
            ))}
          </div>

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <SearchField
              value={query}
              onChange={(value) => setParam("q", value)}
              placeholder="Search unit, branch, or inspector…"
            />
            <BranchSelect
              value={branches.find((b) => b.toLowerCase() === branch.toLowerCase()) || ""}
              onChange={(v) => setParam("branch", v)}
              branches={branches}
            />
            <span className="app-muted text-sm sm:ml-auto tabular-nums">
              {visible.length} of {inBranch.length} unit{inBranch.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="app-card overflow-hidden rounded-2xl border">
            {visible.length === 0 ? (
              <EmptyState
                compact
                icon={ForkliftIcon}
                title="No forklifts match these filters."
                description={forklifts.length ? "Try another branch or status." : "Add a forklift to get started."}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="app-table">
                  <thead>
                    <tr>
                      <SortHeader id="unit" sort={sort} onSort={onSort} className="!px-4">
                        Unit
                      </SortHeader>
                      <SortHeader id="branch" sort={sort} onSort={onSort}>
                        Branch
                      </SortHeader>
                      <th>Status</th>
                      <SortHeader id="last" sort={sort} onSort={onSort}>
                        Last check-in
                      </SortHeader>
                      <SortHeader id="count" sort={sort} onSort={onSort}>
                        Check-ins ({HISTORY_DAYS}d)
                      </SortHeader>
                      <SortHeader id="flagged" sort={sort} onSort={onSort}>
                        Flagged
                      </SortHeader>
                      <th aria-label="Open" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((f) => {
                      const last = lastCheckIn(f);
                      return (
                        <tr
                          key={f.id}
                          tabIndex={0}
                          role="link"
                          aria-label={`Open unit ${f.unit} at ${f.location || "no branch"}`}
                          onClick={() => navigate(`/fleet/forklifts/${f.id}`)}
                          onKeyDown={(e) => e.key === "Enter" && navigate(`/fleet/forklifts/${f.id}`)}
                          className="cursor-pointer focus:outline-none focus-visible:bg-[color:var(--app-hover)]"
                        >
                          <td className="!px-4 whitespace-nowrap">
                            <span className="inline-flex items-center gap-2">
                              <Link
                                to={`/fleet/forklifts/${f.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="font-semibold tabular-nums hover:text-[color:var(--app-accent)]"
                              >
                                #{f.unit}
                              </Link>
                              {f.moffett && <Pill tone="violet">Moffett</Pill>}
                            </span>
                          </td>
                          <td className="whitespace-nowrap">{f.location || <span className="app-muted">No branch</span>}</td>
                          <td className="max-w-[14rem]">
                            <ServiceChip inService={f.inService} service={f.service} />
                            {!f.inService && f.service && (
                              <span className="app-muted mt-0.5 block truncate text-xs" title={f.service.note || undefined}>
                                {serviceSummary(f.service)}
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap">
                            <span className="flex flex-col gap-0.5">
                              <span>
                                <Pill tone={checkinTone(last)}>{relativeDay(last)}</Pill>
                              </span>
                              {last && (
                                <span className="app-muted text-xs tabular-nums">
                                  {formatDateTime(last)}
                                  {f.stats.lastInspector ? ` · ${f.stats.lastInspector}` : ""}
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="tabular-nums">{f.stats.count}</td>
                          <td>
                            {f.stats.flagged > 0 ? (
                              <Pill tone="amber">{f.stats.flagged}</Pill>
                            ) : (
                              <span className="app-muted">—</span>
                            )}
                          </td>
                          <td className="w-8 text-right">
                            <ChevronRightRoundedIcon className="app-muted" fontSize="small" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </PageShell>
  );
};

export default ForkliftList;
